<template>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form">
				<h2 class="item" v-if="isCreatingViewMode">
					Новый сервисный компонент
				</h2>
				<h2 class="item" v-else>
					Карточка сервисного компонента
				</h2>
			</div>
		</el-col>		
	</el-row>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form">
				<el-form class="base-form" :model="ruleForm" ref="jobForm" status-icon :rules="rules">
					<el-form-item label="Компонент (узел)" label-position="top" prop="name">
						<el-input v-model="ruleForm.name" />
					</el-form-item>

					<el-form-item label="Группа компонента" label-position="top" prop="componentGroup">
						<catalog-picker-component
							auth
							ref="jobComponentGroupPicker"
							v-model="ruleForm.componentGroup"
							placeholder="Поиск по названию группы" 
							search-url="/api/servicejobcomponentgroups"
							:catalog-tab="tabs.jobComponentGroups"								
							@catalog-item-picked="onComponentGroupPicked">
							<template #label>
								{{ ruleForm.componentGroup?.name }}
							</template>
							<template #default="scope">
								{{ scope.name }}
							</template>
						</catalog-picker-component>
					</el-form-item>

					<el-form-item label="Сервисная операция по-умолчанию" label-position="top">
						<catalog-picker-component
							auth
							ref="jobComponentDefaultOperationPicker"
							v-model="ruleForm.defaultOperation"
							placeholder="Поиск по названию операции" 
							search-url="/api/servicejoboperations/search"
							:catalog-tab="tabs.jobOperations"								
							@catalog-item-picked="onComponentDefaultOperationPicked">
							<template #label>
								{{ ruleForm.defaultOperation?.name }}
							</template>
							<template #default="scope">
								{{ scope.name }}
							</template>
						</catalog-picker-component>
					</el-form-item>

					<el-form-item label="Описание компонента (узла)" label-position="top" prop="description">
						<el-input v-model="ruleForm.description"
							maxlength="255"
							show-word-limit
							type="textarea" />
					</el-form-item>

					<el-form-item>
						<el-row style="width: 100%;">
							<el-col :span="14">
								<div style="display: inline-block; margin-right: 1rem;">
									<el-button type="primary" :disabled="asyncOperationPending" @click="onFormSubmitClick">
										{{ isCreatingViewMode ? 'Создать компонент' : 'Сохранить' }}
									</el-button>
								</div>
								<div style="display: inline-block;">
									<el-button @click="onFormCancelClick">
										Отменить
									</el-button>
								</div>
							</el-col>
							<el-col :span="10">
								<div v-if="!isCreatingViewMode" style="text-align: right;">
									<el-button type="danger" :disabled="asyncOperationPending" @click="onFormDeleteClick">
										Удалить компонент
									</el-button>
								</div>
							</el-col>
						</el-row>
						
						<el-row style="width: 100%">
							<el-col :span="10">
								&nbsp;
							</el-col>
							<el-col :span="14">
								<div v-if="!isCreatingViewMode && !isDeletionAllowed" style="text-align: right; line-height: 1rem; margin-top: 1rem">
									<el-text type="info" size="small">
										<el-icon><Opportunity /></el-icon>
										Компонент нельзя удалить - на него ссылаются сервисные работы (ссылок {{referencedJobsCount}})
									</el-text>
								</div>
							</el-col>
						</el-row>
					</el-form-item>
				</el-form>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import CatalogPickerComponent from '@/components/CatalogPickerComponent.vue';
import { ElButton, ElTable, ElLink, FormInstance, ElNotification, ElMessageBox } from 'element-plus';
import { Opportunity } from '@element-plus/icons-vue';
import { reactive } from 'vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import tabs from '@/variables/tabs';
import EventHelper from '@/helpers/eventsHelper';
import ServiceJobComponentModel from '@/models/ServiceJobComponentModel';
import ServiceJobComponentGroupModel from '@/models/ServiceJobComponentGroupModel';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';


interface IServiceJobComponentFormFields {
	id: number;
	name: string | null;
	description: string | null;
	componentGroup: ServiceJobComponentGroupModel | null,
	defaultOperation: ServiceJobOperationModel | null
}

@Options({
	components: {
		ElButton, ElTable, ElLink, CatalogPickerComponent, Opportunity
	},
	props: {
		params: Array
	}
})
export default class ServiceJobComponentView extends Vue {
	/** params */
	params!: Array<any>;
	isCreatingViewMode = false;

	/** constants */
	tabs = tabs;
	serviceComponent: ServiceJobComponentModel | null = null;
	
	/** view-models */
	isDeletionAllowed = false;
	referencedJobsCount = 0;
	asyncOperationPending = false;
	ruleForm = reactive<IServiceJobComponentFormFields>({
		id: 0,
		name: null,
		description: null,
		componentGroup: null,
		defaultOperation: null
	});

	rules = reactive({
		name: [
			{ required: true, message: 'Название компонента не может быть пустым'},
			{ min: 1, max: 255, message: 'Название компонента не может быть короче 1 и длинее 255 символов'},			
		],
		description: [
			{ max: 255, message: 'Описание компонента не может быть длинее 255 символов'},			
		]
	});
	
	mounted() {
		this.initForm();
	}

	/**
	 * init form with service-job-component's data from incoming params
	 */
	async initForm() {
		if (!this.params?.length ||
			this.params[1] === true) {
			this.isCreatingViewMode = true;
			return;
		}

		if (this.params[1] === false &&
			this.params[0]?.id) {	
			this.isCreatingViewMode = false;			
			this.serviceComponent = new ServiceJobComponentModel(this.params[0]);
			this.ruleForm.id = this.serviceComponent.id;
			this.ruleForm.name = this.serviceComponent.name;
			this.ruleForm.description = this.serviceComponent.description;
			this.ruleForm.componentGroup = this.serviceComponent.componentGroup;				
			if (this.serviceComponent.componentGroup?.id) {
				this.setComponentGroupPicked(this.serviceComponent.componentGroup);
			}
			
			if (this.serviceComponent.defaultOperation?.id) {
				this.setComponentDefaultOperation(this.serviceComponent.defaultOperation);
			}			

			const relatedJobIdsToGroup = await this.getRelatedJobIdsAsync(this.serviceComponent.id);
			this.referencedJobsCount = relatedJobIdsToGroup?.length ?? 0;
			this.isDeletionAllowed = relatedJobIdsToGroup?.length === 0;
		}
	}

	async onFormSubmitClick() {
		const form = this.$refs.jobForm as FormInstance;
		form.validate(async (valid) => {
			if (valid) {
				try {
					this.asyncOperationPending = true;
					if (this.isCreatingViewMode) {
						await this.addComponent();
					} else {
						await this.updateComponent();
					}
					ElNotification({
						message: `Сервисный компонет успешно ${this.isCreatingViewMode ? 'создан' : 'обновлен'}`,
						type: 'success',
					});
					EventHelper.dispatchEvent('jobComponentsUpdate');
					this.closeCurrentTab();
				} catch (e: any) {
					if (e.code == 'ERR_NETWORK') {
						ElNotification({
							message: `Ошибка соединения, не удалось ${this.isCreatingViewMode ? 'создать' : 'обновить'} сервисный компонет`,
							type: 'error',
						});
					} else {
						ElNotification({
							message: `Ошибка ${this.isCreatingViewMode ? 'создания нового' : 'обновления'} сервисного компонета`,
							type: 'error',
						});
					}
					console.error('Error submitting service-job-component', e);
				}
				finally {
					this.asyncOperationPending = false;
				}
			}			
		});
	}

	onFormCancelClick() {
		this.closeCurrentTab();
	}

	/** deletes an existing service-job-component */
	async onFormDeleteClick() {
		const relatedJobIdsToCategory = await this.getRelatedJobIdsAsync(this.serviceComponent!.id);

		if (relatedJobIdsToCategory === null) {
			ElNotification({
				message: 'Ошибка удаления сервисного компонента',
				type: 'error',
			});
			return;
		}

		if (relatedJobIdsToCategory.length == 0) {
			ElMessageBox.confirm(
				`Компонент '${this.serviceComponent!.name}' будет удален`,
				{
					confirmButtonText: 'OK',
					cancelButtonText: 'Отмена',
					type: 'error',
				}
			)
			.then(() => this.deleteComponentAsync(this.serviceComponent!.id, this.serviceComponent!.name));
		} else {
			ElMessageBox.alert(
				`Невозможно удалить компонент '${this.serviceComponent!.name}', на него ссылаются сервисные работы (${relatedJobIdsToCategory.length})`,
				'Удаление'
			);
		}
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}

	async addComponent() {
		const request = {
			name: this.ruleForm.name,
			description: this.ruleForm.description,
			groupId: this.ruleForm.componentGroup?.id ?? null,
			defaultOperationId: this.ruleForm.defaultOperation?.id ?? null
		};
		const jwt = store.getters.jwt;
		const a = axiosHelper.createInstance(jwt);
		await a.post('/api/servicejobcomponents', request);
	}

	async updateComponent() {
		const request = {
			id: this.ruleForm.id,
			name: this.ruleForm.name,
			description: this.ruleForm.description,
			groupId: this.ruleForm.componentGroup?.id ?? null,
			defaultOperationId: this.ruleForm.defaultOperation?.id ?? null
		};
		const jwt = store.getters.jwt;
		const a = axiosHelper.createInstance(jwt);
		await a.put('/api/servicejobcomponents', request);
	}

	async deleteComponentAsync(componentId: number, componentName: string) {
		try {
			this.asyncOperationPending = true;
			const jwt = store.getters.jwt;
			const a = axiosHelper.createInstance(jwt);
			await a.delete(`/api/servicejobcomponents/${componentId}`);
			ElNotification({
				title: `Сервисный компонент '${componentName}' удален`,
				type: 'success',
			});
			EventHelper.dispatchEvent('jobComponentsUpdate');
			this.closeCurrentTab();	
			this.asyncOperationPending = false;
		} catch (e) {
			ElNotification({
				title: 'Ошибка, сервисный компонент не был удален',
				type: 'error',
			});
			console.error(`Error deleting service-job-component #${componentId}`, e)
		} finally {
			this.asyncOperationPending = false;
		}
	}

	onComponentGroupPicked(jobComponentGroup: ServiceJobComponentGroupModel) {
		this.setComponentGroupPicked(jobComponentGroup);
	}

	onComponentDefaultOperationPicked(componentDefaultOperation: ServiceJobOperationModel) {
		this.setComponentDefaultOperation(new ServiceJobOperationModel(componentDefaultOperation));
	}

	setComponentGroupPicked(jobComponentGroup: ServiceJobComponentGroupModel) {
		this.ruleForm.componentGroup = jobComponentGroup;		
		const jobComponentGroupPicker = this.$refs.jobComponentGroupPicker as CatalogPickerComponent;
		jobComponentGroupPicker.set(jobComponentGroup);
	}

	setComponentDefaultOperation(jobComponentDefOperation: ServiceJobOperationModel) {
		this.ruleForm.defaultOperation = jobComponentDefOperation;		
		const jobComponentGroupPicker = this.$refs.jobComponentDefaultOperationPicker as CatalogPickerComponent;
		jobComponentGroupPicker.set(jobComponentDefOperation);
	}

	async getRelatedJobIdsAsync(componentId: number): Promise<Array<number> | null> {
		try {
			const jwt = store.getters.jwt;
			const jobCategoriesHttpClient = axiosHelper.createInstance(jwt);
			const jobCategoriesResponse = await jobCategoriesHttpClient.get(`/api/servicejobcomponents/rels/${componentId}`);
			return jobCategoriesResponse.data.map((_: number) => _);
		} catch (e) {			
			console.error(`Error checking service-job-component #${componentId} for related jobs`, e);
			return null;
		}
	}
}
</script>


<style scoped>
h2.item span {
	color: var(--el-color-primary);
}
</style>